// src/component/page/messenger/message/ModernMessageInput.tsx
import React, { useRef, useState, useCallback, useEffect } from 'react';
import { useAtom } from 'jotai';
import {
  Box,
  Flex,
  HStack,
  VStack,
  Button,
  IconButton,
  Grid,
  Text,
  createToaster,
  Badge
} from '@chakra-ui/react';
import {
  Plus,
  Paperclip,
  Image,
  File,
  Sparkles,
  Send,
  X,
  ChevronDown,
  Bot,
  Users,
  Zap,
  Settings
} from 'lucide-react';
import styled from 'styled-components';

// Store imports
import {
  selectedChannelAtom,
  messageInputAtom,
  mentionedUsersAtom,
  showAttachmentsAtom,
} from '../../../../stores/messengerStore/messengerStore';

// Hook imports
import { useChatManager } from '../../../../hooks/useChatManager';
import { useWebSocket } from '../../../../hooks/useWebSocket';
import { useFileUpload } from '../../../../hooks/useMessengerQueries';

const toaster = createToaster({
  placement: "top",
});

// AI 모델 타입
type AIModel = {
  id: string;
  name: string;
  description: string;
  badge?: string;
};

const AI_MODELS: AIModel[] = [
  { id: 'gpt-4', name: 'GPT-4', description: '가장 강력한 AI 모델', badge: 'Pro' },
  { id: 'gpt-3.5', name: 'GPT-3.5', description: '빠르고 효율적인 모델' },
  { id: 'claude', name: 'Claude', description: 'Anthropic의 AI 어시스턴트' },
];

const ModernMessageInput: React.FC = () => {
  const textareaRef = useRef<HTMLDivElement>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);
  
  // Atoms
  const [selectedChannel] = useAtom(selectedChannelAtom);
  const [messageInput, setMessageInput] = useAtom(messageInputAtom);
  const [mentionedUsers, setMentionedUsers] = useAtom(mentionedUsersAtom);
  const [showAttachments, setShowAttachments] = useAtom(showAttachmentsAtom);
  
  // Local state
  const [isAIMode, setIsAIMode] = useState(false);
  const [selectedModel, setSelectedModel] = useState(AI_MODELS[0]);
  const [showModelSelector, setShowModelSelector] = useState(false);
  const [showToolsMenu, setShowToolsMenu] = useState(false);
  const [localMessageInput, setLocalMessageInput] = useState('');
  
  // Hooks
  const { handleSendMessage, isSendingMessage } = useChatManager();
  const { startTyping, stopTyping } = useWebSocket();
  const fileUploadMutation = useFileUpload();
  
  // 타이핑 타이머 관리
  const typingTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  
  // 로컬 입력 변경 핸들러
  const handleLocalInputChange = useCallback((text: string) => {
    setLocalMessageInput(text);
    setMessageInput(text);
    
    // 타이핑 이벤트 처리
    if (selectedChannel && !isAIMode) {
      startTyping(selectedChannel.id);
      
      if (typingTimeoutRef.current) {
        clearTimeout(typingTimeoutRef.current);
      }
      
      typingTimeoutRef.current = setTimeout(() => {
        stopTyping(selectedChannel.id);
      }, 2000);
    }
  }, [selectedChannel, setMessageInput, startTyping, stopTyping, isAIMode]);
  
  // contenteditable 입력 핸들러
  const handleInput = useCallback((e: React.FormEvent<HTMLDivElement>) => {
    const text = e.currentTarget.textContent || '';
    handleLocalInputChange(text);
  }, [handleLocalInputChange]);

// Enter 키 핸들러
  const handleKeyDown = useCallback((e: React.KeyboardEvent<HTMLDivElement>) => {
    if (e.key === 'Enter' && !e.shiftKey && !e.nativeEvent.isComposing) {
      e.preventDefault();
      
      const currentText = textareaRef.current?.textContent?.trim() || '';
      
      if (currentText) {
        handleSendMessage();
        // 입력창 초기화
        if (textareaRef.current) {
          textareaRef.current.textContent = '';
        }
        setLocalMessageInput('');
        setMessageInput('');
        
        if (selectedChannel && !isAIMode) {
          stopTyping(selectedChannel.id);
        }
        if (typingTimeoutRef.current) {
          clearTimeout(typingTimeoutRef.current);
        }
      }
    }
  }, [handleSendMessage, selectedChannel, stopTyping, isAIMode, setMessageInput]);
  
  // 파일 선택 핸들러
  const handleFileSelect = useCallback((files: FileList | null) => {
    if (!files || !selectedChannel) return;
    
    Array.from(files).forEach(file => {
      if (file.size > 10 * 1024 * 1024) {
        toaster.create({
          title: '파일 크기 초과',
          description: '10MB 이하의 파일만 업로드 가능합니다.',
          status: 'error',
          duration: 3000
        });
        return;
      }
      
      fileUploadMutation.mutate(file, {
        onSuccess: (response) => {
          const fileMessage = {
            channelId: selectedChannel.id,
            message: file.name,
            messageType: file.type.startsWith('image/') ? 'IMAGE' as const : 'FILE' as const,
            attachments: [{
              fileName: file.name,
              fileUrl: response.data.fileUrl,
              mimeType: file.type,
              fileSize: file.size
            }]
          };
          
          handleSendMessage();
        },
        onError: () => {
          toaster.create({
            title: '파일 업로드 실패',
            description: '파일 업로드 중 오류가 발생했습니다.',
            status: 'error',
            duration: 3000
          });
        }
      });
    });
  }, [selectedChannel, fileUploadMutation, handleSendMessage]);
  
  // AI 모드 토글
  const toggleAIMode = useCallback(() => {
    setIsAIMode(prev => !prev);
    toaster.create({
      title: !isAIMode ? 'AI 어시스턴트 모드' : '일반 채팅 모드',
      description: !isAIMode ? 'AI와 대화할 수 있습니다' : '일반 사용자와 대화합니다',
      status: 'info',
      duration: 2000
    });
  }, [isAIMode]);
  
  if (!selectedChannel) {
    return (
      <StyledEmptyState>
        <Text color="gray.500">채널을 선택해주세요</Text>
      </StyledEmptyState>
    );
  }
  
  return (
    <StyledInputContainer isAIMode={isAIMode}>
      {/* 첨부파일 메뉴 */}
      {showAttachments && (
        <StyledAttachmentMenu>
          <Flex justify="space-between" align="center" mb={3}>
            <Text fontSize="sm" fontWeight="600">파일 첨부</Text>
            <IconButton
              aria-label="닫기"
              size="xs"
              variant="ghost"
              onClick={() => setShowAttachments(false)}
            >
              <X size={14} />
            </IconButton>
          </Flex>
          <Grid templateColumns="repeat(3, 1fr)" gap={2}>
            <StyledMenuButton onClick={() => fileInputRef.current?.click()}>
              <File size={20} />
              <span>파일</span>
            </StyledMenuButton>
            <StyledMenuButton
              onClick={() => {
                if (fileInputRef.current) {
                  fileInputRef.current.accept = 'image/*';
                  fileInputRef.current.click();
                }
              }}
            >
              <Image size={20} />
              <span>이미지</span>
            </StyledMenuButton>
            <StyledMenuButton onClick={toggleAIMode}>
              <Sparkles size={20} color={isAIMode ? '#8B5CF6' : 'currentColor'} />
              <span>AI 모드</span>
            </StyledMenuButton>
          </Grid>
        </StyledAttachmentMenu>
      )}
      
      {/* 도구 메뉴 */}
      {showToolsMenu && (
        <StyledToolsMenu>
          <Flex justify="space-between" align="center" mb={3}>
            <Text fontSize="sm" fontWeight="600">도구</Text>
            <IconButton
              aria-label="닫기"
              size="xs"
              variant="ghost"
              onClick={() => setShowToolsMenu(false)}
            >
              <X size={14} />
            </IconButton>
          </Flex>
          <VStack align="stretch" gap={1}>
            <StyledToolOption>
              <Zap size={16} />
              <Text fontSize="sm">빠른 답변</Text>
              <Badge size="xs" colorScheme="purple" ml="auto">Beta</Badge>
            </StyledToolOption>
            <StyledToolOption>
              <Settings size={16} />
              <Text fontSize="sm">설정</Text>
            </StyledToolOption>
          </VStack>
        </StyledToolsMenu>
      )}
      
      {/* 모델 선택기 */}
      {showModelSelector && isAIMode && (
        <StyledModelSelector>
          <Text fontSize="xs" fontWeight="600" color="gray.600" mb={2}>AI 모델 선택</Text>
          {AI_MODELS.map(model => (
            <StyledModelOption
              key={model.id}
              onClick={() => {
                setSelectedModel(model);
                setShowModelSelector(false);
              }}
              isSelected={selectedModel.id === model.id}
            >
              <Box>
                <Flex align="center" gap={2}>
                  <Text fontSize="sm" fontWeight="500">{model.name}</Text>
                  {model.badge && (
                    <Badge size="xs" colorScheme="purple">{model.badge}</Badge>
                  )}
                </Flex>
                <Text fontSize="xs" color="gray.500">{model.description}</Text>
              </Box>
            </StyledModelOption>
          ))}
        </StyledModelSelector>
      )}
      
      {/* 메인 입력 영역 */}
      <Box bg={"#0f1414"} padding={"0.75rem 1rem 1rem"} borderRight={"1px solid rgba(255, 255, 255, 0.06)"} borderLeft={"1px solid rgba(255, 255, 255, 0.06)"}>
        <StyledInputWrapper isAIMode={isAIMode}>
          {/* 입력 필드 */}
          <StyledContentEditable
            ref={textareaRef}
            contentEditable
            role="textbox"
            aria-label={isAIMode ? 'AI에게 질문하기' : `#${selectedChannel.name}에 메시지 보내기`}
            aria-multiline="true"
            onInput={handleInput}
            onKeyDown={handleKeyDown}
            data-placeholder={
              isAIMode
                ? 'AI에게 무엇이든 물어보세요...'
                : `#${selectedChannel.name}에 메시지 보내기...`
            }
            suppressContentEditableWarning
          />
          
          {/* 하단 컨트롤 */}
          <StyledBottomControls>
            <HStack gap={1}>
              {/* 왼쪽 버튼들 */}
              <StyledIconButton
                onClick={() => setShowAttachments(!showAttachments)}
                isActive={showAttachments}
                title="첨부파일"
              >
                <Plus size={16} />
              </StyledIconButton>
              
              <StyledIconButton
                onClick={() => setShowToolsMenu(!showToolsMenu)}
                isActive={showToolsMenu}
                title="도구"
              >
                <Settings size={16} />
              </StyledIconButton>
              
              {/* AI 모드 토글 */}
              <StyledAIToggle
                onClick={toggleAIMode}
                isActive={isAIMode}
                title={isAIMode ? 'AI 모드 끄기' : 'AI 모드 켜기'}
              >
                {isAIMode ? <Bot size={16} /> : <Users size={16} />}
              </StyledAIToggle>
            </HStack>
            
            {/* 오른쪽 영역 */}
            <HStack gap={2}>
              {/* AI 모델 선택 (AI 모드일 때만) */}
              {isAIMode && (
                <StyledModelButton
                  onClick={() => setShowModelSelector(!showModelSelector)}
                >
                  <span>{selectedModel.name}</span>
                  <ChevronDown size={14} />
                </StyledModelButton>
              )}
              
              {/* 전송 버튼 */}
              <StyledSendButton
                disabled={!localMessageInput.trim() || isSendingMessage}
                onClick={() => {
                  if (localMessageInput.trim()) {
                    handleSendMessage();
                    if (textareaRef.current) {
                      textareaRef.current.textContent = '';
                    }
                    setLocalMessageInput('');
                    setMessageInput('');
                  }
                }}
                isAIMode={isAIMode}
                title="메시지 보내기"
              >
                <Send size={16} />
              </StyledSendButton>
            </HStack>
          </StyledBottomControls>
        </StyledInputWrapper>
      </Box>
      
      {/* 숨겨진 파일 입력 */}
      <input
        ref={fileInputRef}
        type="file"
        multiple
        style={{ display: 'none' }}
        onChange={(e) => handleFileSelect(e.target.files)}
        accept="image/*,video/*,audio/*,.pdf,.doc,.docx,.txt"
      />
    </StyledInputContainer>
  );
};

export default ModernMessageInput;

// Styled Components
const StyledInputContainer = styled(Box)<{ isAIMode: boolean }>`
  position: relative;
  bg: '#fafafa';
  transition: all 0.3s ease;
`;

const StyledEmptyState = styled(Box)`
  padding: 1.5rem;
  text-align: center;
  background: #0f1414;
  border-top: 1px solid rgba(255, 255, 255, 0.06);
  color: #94a3a0;
`;

const StyledAttachmentMenu = styled(Box)`
  position: absolute;
  bottom: 100%;
  left: 1rem;
  right: 1rem;
  margin-bottom: 0.5rem;
  padding: 1rem;
  background: #14191a;
  border-radius: 12px;
  box-shadow: 0 8px 24px rgba(0, 0, 0, 0.5);
  border: 1px solid rgba(255, 255, 255, 0.08);
  z-index: 10;
  animation: slideUp 0.2s ease;

  @keyframes slideUp {
    from {
      opacity: 0;
      transform: translateY(10px);
    }
    to {
      opacity: 1;
      transform: translateY(0);
    }
  }
`;

const StyledToolsMenu = styled(StyledAttachmentMenu)`
  left: auto;
  right: 1rem;
  width: 250px;
`;

const StyledMenuButton = styled.button`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 0.25rem;
  padding: 0.75rem;
  background: transparent;
  border: 1px solid rgba(255, 255, 255, 0.1);
  border-radius: 8px;
  cursor: pointer;
  transition: all 0.2s;

  &:hover {
    background: rgba(46, 87, 62, 0.18);
    border-color: rgba(80, 107, 92, 0.55);
    transform: translateY(-1px);
  }

  span {
    font-size: 0.75rem;
    color: #c7d2cc;
  }
`;

const StyledToolOption = styled(Flex)`
  align-items: center;
  gap: 0.5rem;
  padding: 0.5rem 0.75rem;
  border-radius: 6px;
  cursor: pointer;
  transition: background 0.2s;
  color: #c7d2cc;

  &:hover {
    background: rgba(46, 87, 62, 0.18);
    color: #ffffff;
  }
`;

const StyledModelSelector = styled(Box)`
  position: absolute;
  bottom: 100%;
  right: 1rem;
  margin-bottom: 0.5rem;
  padding: 0.5rem;
  background: #14191a;
  border-radius: 8px;
  box-shadow: 0 8px 24px rgba(0, 0, 0, 0.5);
  border: 1px solid rgba(255, 255, 255, 0.08);
  z-index: 20;
  min-width: 200px;
`;

const StyledModelOption = styled(Box)<{ isSelected: boolean }>`
  padding: 0.5rem;
  border-radius: 6px;
  cursor: pointer;
  background: ${props => props.isSelected ? 'rgba(46, 87, 62, 0.28)' : 'transparent'};
  color: #e8eaeb;
  transition: background 0.2s;

  &:hover {
    background: rgba(46, 87, 62, 0.18);
  }
`;

const StyledMainInput = styled(Box)`
  padding: 0.75rem 1rem 1rem;
  background: #0f1414;
`;

const StyledInputWrapper = styled(Box)<{ isAIMode: boolean }>`
  display: flex;
  flex-direction: column;
  background: #14191a;
  border: 1px solid ${props => props.isAIMode ? 'rgba(182, 160, 230, 0.55)' : 'rgba(255, 255, 255, 0.1)'};
  border-radius: 16px;
  transition: all 0.2s;

  &:focus-within {
    border-color: ${props => props.isAIMode ? '#b6a0e6' : 'rgba(80, 107, 92, 0.65)'};
    box-shadow: 0 0 0 3px ${props => props.isAIMode ?
  'rgba(182, 160, 230, 0.15)' :
  'rgba(46, 87, 62, 0.18)'};
  }
`;

const StyledContentEditable = styled.div`
  min-height: 3rem;
  max-height: 10rem;
  overflow-y: auto;
  padding: 0.75rem 1rem;
  font-size: 0.95rem;
  line-height: 1.5;
  color: #ffffff;
  outline: none;

  &:empty::before {
    content: attr(data-placeholder);
    color: rgba(255, 255, 255, 0.3);
    pointer-events: none;
  }

  &::-webkit-scrollbar {
    width: 6px;
  }

  &::-webkit-scrollbar-track {
    background: transparent;
    border-radius: 3px;
  }

  &::-webkit-scrollbar-thumb {
    background: rgba(255, 255, 255, 0.15);
    border-radius: 3px;

    &:hover {
      background: rgba(255, 255, 255, 0.25);
    }
  }
`;

const StyledBottomControls = styled(Flex)`
  justify-content: space-between;
  align-items: center;
  padding: 0.5rem 0.75rem;
  border-top: 1px solid rgba(255, 255, 255, 0.06);
`;

const StyledIconButton = styled.button<{ isActive?: boolean }>`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 32px;
  height: 32px;
  border-radius: 8px;
  background: ${props => props.isActive ? 'rgba(46, 87, 62, 0.28)' : 'transparent'};
  color: ${props => props.isActive ? '#7fb89a' : '#94a3a0'};
  border: 1px solid transparent;
  cursor: pointer;
  transition: all 0.2s;

  &:hover {
    background: rgba(46, 87, 62, 0.18);
    color: #c7d2cc;
  }
`;

const StyledAIToggle = styled(StyledIconButton)`
  background: ${props => props.isActive ?
  'linear-gradient(135deg, #5e4fb8, #6f4a9f)' :
  'transparent'};
  color: ${props => props.isActive ? 'white' : '#94a3a0'};

  &:hover {
    background: ${props => props.isActive ?
  'linear-gradient(135deg, #6f5ec8, #7d5aaf)' :
  'rgba(255, 255, 255, 0.06)'};
    color: ${props => props.isActive ? 'white' : '#c7d2cc'};
  }
`;

const StyledModelButton = styled.button`
  display: flex;
  align-items: center;
  gap: 0.25rem;
  padding: 0.25rem 0.75rem;
  background: rgba(255, 255, 255, 0.05);
  border: 1px solid rgba(255, 255, 255, 0.1);
  border-radius: 8px;
  font-size: 0.875rem;
  color: #c7d2cc;
  cursor: pointer;
  transition: all 0.2s;

  &:hover {
    background: rgba(46, 87, 62, 0.18);
    border-color: rgba(80, 107, 92, 0.45);
    color: #ffffff;
  }
`;

const StyledSendButton = styled.button<{ isAIMode: boolean }>`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 32px;
  height: 32px;
  border-radius: 8px;
  background: ${props => props.isAIMode ?
  'linear-gradient(135deg, #5e4fb8, #6f4a9f)' :
  '#2f5743'};
  color: white;
  border: 1px solid ${props => props.isAIMode ? 'rgba(182, 160, 230, 0.45)' : 'rgba(80, 107, 92, 0.55)'};
  cursor: pointer;
  transition: all 0.2s;

  &:hover:not(:disabled) {
    background: ${props => props.isAIMode ?
  'linear-gradient(135deg, #6f5ec8, #7d5aaf)' :
  '#386851'};
    transform: scale(1.05);
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.45);
  }

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }

  &:active:not(:disabled) {
    transform: scale(0.95);
  }
`;